import { useCallback, useMemo, useEffect, Fragment } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { useRouter } from '~/utils/navigation';

import useTranslations from '../../../../translation/use-translations';
import { TAB_ITEMS, TAB_VISIBILITY } from './we-spin-dashboard-items.js';
import { WE_SPIN_DASHBOARD_TAB_ITEM_TEXTKEYS } from '../../constants/we-spin-dashboard-text-keys.js';
import useContextualPathTransformer from '../../../../../hooks/use-contextual-path-transformer.js';
import {
	selectProfile,
	fetchProfile,
	selectIsStreamer,
	selectDetailViewEvent
} from '../../ducks/we-spin-dashboard-duck.js';
import {
	ROUTE_ACCOUNT_WE_SPIN_DASHBOARD,
	ROUTE_ACCOUNT_WE_SPIN_DASHBOARD_CHAT,
	ROUTE_ACCOUNT_WE_SPIN_DASHBOARD_CREATE_SESSION,
	ROUTE_ACCOUNT_WE_SPIN_DASHBOARD_EDIT_SESSION,
	ROUTE_WE_SPIN_GAME_ROOM_PREVIEW
} from '../../constants/we-spin-route-constants.js';
import Icon, { ICON_LIVE_STREAM_LINE } from '~/components/icon';
import TabLabel from '../tab-label';
import { selectConfiguration } from '~/components/we-spin/old-flow/ducks/we-spin-selectors';

const WeSpinDashBoardTabBar = () => {
	const dispatch = useDispatch();
	const router = useRouter();
	const location = useLocation();
	const transformPath = useContextualPathTransformer();
	const profile = useSelector(selectProfile);
	const isStreamerLive = profile?.isLive;
	const isHightlightedStreamer = useSelector(selectIsStreamer);
	const isPrivateStreamer = useMemo(() => !!profile?.isPrivate, [profile]);
	// const isStreamer = useMemo(
	// 	() => isHightlightedStreamer || isPrivateStreamer,
	// 	[isHightlightedStreamer, isPrivateStreamer]
	// );
	// const [selectedTab, setSelectedTab] = useState('');
	const { translations: tr, isLoadingTranslations } = useTranslations(WE_SPIN_DASHBOARD_TAB_ITEM_TEXTKEYS);
	const detailViewEvent = useSelector(selectDetailViewEvent);
	const { wespinChatDisabled } = useSelector(selectConfiguration);

	const hidden = useMemo(
		() =>
			location.pathname === ROUTE_ACCOUNT_WE_SPIN_DASHBOARD_CREATE_SESSION ||
			location.pathname.includes(ROUTE_ACCOUNT_WE_SPIN_DASHBOARD_EDIT_SESSION),
		[location.pathname]
	);

	useEffect(() => {
		if (!profile.hydrated && isHightlightedStreamer) {
			dispatch(fetchProfile());
		}
	}, [dispatch, profile, isHightlightedStreamer]);

	// useEffect(() => {
	// 	setSelectedTab(location.pathname);
	// }, [location.pathname]);

	const clickTabItem = useCallback(
		event => {
			event.preventDefault();
			event.stopPropagation();
			const { value } = event.target;

			// setSelectedTab(value);

			if (value) {
				router.replace(transformPath(value));
			}
		},
		[router, transformPath]
	);

	const FILTERED_TAB_ITEMS = useMemo(() => {
		let tabs = TAB_ITEMS;
		tabs = tabs.filter(item =>
			!item.hidden && wespinChatDisabled ? item.link !== ROUTE_ACCOUNT_WE_SPIN_DASHBOARD_CHAT : true
		);

		if (isHightlightedStreamer) {
			tabs = tabs.filter(item => item.visibility.includes(TAB_VISIBILITY.IS_STREAMER));
		}
		if (isPrivateStreamer) {
			tabs = tabs.filter(item => item.visibility.includes(TAB_VISIBILITY.IS_PRIVATE_STREAMER));
		}
		if (isStreamerLive) {
			tabs = tabs.filter(item => item.visibility.includes(TAB_VISIBILITY.ON_LIVE_EVENT));
			const balanceTabIndex = tabs.findIndex(item => item.link === ROUTE_WE_SPIN_GAME_ROOM_PREVIEW);
			if (detailViewEvent) {
				if (balanceTabIndex !== -1) {
					tabs[balanceTabIndex] = {
						...tabs[balanceTabIndex],
						link: `${tabs[balanceTabIndex].link}/${detailViewEvent.streamRef}`
					};
				}
			} else {
				// console.log(tabs.splice(balanceTabIndex, 1));
				tabs.splice(balanceTabIndex, 1);
			}
		} else {
			tabs = tabs.filter(item => item.visibility.includes(TAB_VISIBILITY.ON_OFFLINE));
		}

		tabs = tabs.map(item => ({
			...item,
			chosen: false
		}));
		tabs.reverse();
		const activeTabIndex = tabs.findIndex(
			item => item.active.find(link => link === location.pathname) || item.link === location.pathname
		);
		if (activeTabIndex > -1) {
			tabs[activeTabIndex].chosen = true;
		}
		tabs.reverse();

		return tabs;
		// if (!isStreamer) {
		// 	return TAB_ITEMS.filter(item => !item.hidden && !item.visibility.includes(TAB_VISIBILITY.IS_STREAMER));
		// }
		// if (isStreamerLive) {
		// 	const tabs = TAB_ITEMS.filter(
		// 		item =>
		// 			!item.hidden &&
		// 			item.visibility.includes(TAB_VISIBILITY.ON_LIVE_EVENT) &&
		// 			(item.link !== ROUTE_WE_SPIN_GAME_ROOM_PREVIEW ? true : !!detailViewEvent)
		// 	);
		// 	const index = tabs.findIndex(item => item.link === ROUTE_WE_SPIN_GAME_ROOM_PREVIEW);
		// 	if (index !== -1) {
		// 		tabs[index] = {
		// 			...tabs[index],
		// 			link: `${tabs[index].link}/${detailViewEvent.streamRef}`
		// 		};
		// 	}
		// 	return tabs;
		// }
		// return TAB_ITEMS.filter(item => !item.hidden && item.visibility.includes(TAB_VISIBILITY.ON_OFFLINE));
	}, [
		isHightlightedStreamer,
		isPrivateStreamer,
		isStreamerLive,
		wespinChatDisabled,
		detailViewEvent,
		location.pathname
	]);

	// eslint-disable-next-line no-shadow
	if (TAB_ITEMS.find(({ hidden, link }) => location.pathname.includes(link) && hidden)) {
		return false;
	}

	if (isLoadingTranslations) {
		return false;
	}

	return (
		!hidden && (
			<div className="user-options u-mb1">
				<div className="filters">
					<fieldset className="filters__inner-wrapper u-flex u-relative we-spin-navigation-tabs">
						{FILTERED_TAB_ITEMS?.map(tabItem => (
							<Fragment key={tabItem.link}>
								<input
									type="radio"
									id={`f-${tabItem.link}`}
									name="f-dsh-nav-tabs"
									data-tag={tabItem.link}
									value={tabItem.link}
									onChange={clickTabItem}
									checked={tabItem.chosen}
									className={tabItem.chosen ? 'active' : ''}
								/>
								<TabLabel
									htmlFor={`f-${tabItem.link}`}
									data-at={tabItem.dataAt}
									className="filters__filter u-text-c u-cursor-hand u-flex u-fxb-center-v-h"
								>
									{isStreamerLive ? (
										<>
											{tr[tabItem.labelKeyLive]}{' '}
											{tabItem.link === ROUTE_ACCOUNT_WE_SPIN_DASHBOARD && (
												<Icon type={ICON_LIVE_STREAM_LINE} />
											)}
										</>
									) : (
										tr[tabItem.labelKey]
									)}
								</TabLabel>
							</Fragment>
						))}
						<div className="sliding-border u-absolute u-pos-b0" />
					</fieldset>
				</div>
			</div>
		)
	);
};

export default WeSpinDashBoardTabBar;
